import { useState } from 'react';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';
import { useAppNotification } from '../../../../notifications/NotificationContextProvider';
import {
  valueFromFiatToAsset,
  useHandleGqlErrorsWithSnackbar,
} from '../../../../common';
import { datadogErrorHelper } from '../../../../common/utils/datadogErrorHelper';
import { TTransfersNextStepStateWithActions } from '../../../utils/useTransfersNextStepState.client';
import { TransfersSendStepsIds } from '../../../types';
import { createCryptoWithdrawalOffer } from '../actions/createCryptoWithdrawalOffer.action';
import { useWithdrawalAmountInput } from './useWithdrawalAmountInput';

interface HookProps {
  cryptoTransferState: TTransfersNextStepStateWithActions;
}

export const useWithdrawalForm = ({ cryptoTransferState }: HookProps) => {
  const { setAppNotification } = useAppNotification();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const {
    selectedAssetData,
    selectedNetworkData,
    bindCreatedWithdrawalOfferData,
    changeToPageAndBindActions,
    setAssetAmountType,
    selectedWalletData,
  } = cryptoTransferState;

  const [isLoading, setIsLoading] = useState(false);

  const {
    amountInputValue,
    amountInputValueWithMinFees,
    amountInputValueType,
    amountInputErrors,
    validateInputAndCheckForThresholdsErrors,
    changeAmountValueType,
  } = useWithdrawalAmountInput({ selectedAssetData, selectedNetworkData });

  const onSubmitCallCreateOfferMutation = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    const selectedAmount =
      amountInputValueType === 'FIAT'
        ? valueFromFiatToAsset(
            amountInputValue,
            selectedAssetData?.price,
            selectedNetworkData?.maximumTransactionPrecision,
            true,
          )?.value
        : amountInputValue;

    setIsLoading(true);

    try {
      const createdCryptoWithdrawalResponse = await createCryptoWithdrawalOffer(
        {
          assetAmount: selectedAmount,
          externalAddressID: selectedWalletData?.id as string,
        },
      );

      const errors = createdCryptoWithdrawalResponse?.errors;

      if (errors) {
        processErrors(errors);

        setIsLoading(false);

        return;
      }

      bindCreatedWithdrawalOfferData(
        createdCryptoWithdrawalResponse.data.createCryptoWithdrawal,
      );

      setAssetAmountType(amountInputValueType);

      changeToPageAndBindActions({
        pageId: TransfersSendStepsIds.Confirmation,
        newBackAction: {
          pageId: TransfersSendStepsIds.ChooseAmount,
          type: 'page',
        },
      });
    } catch (_e) {
      setAppNotification({
        title: 'ERROR',
        subtitle:
          '[MISSING TRANSLATION] Failed to create crypto transfer withdrawal offer.',
        customDuration: 5000,
        withClose: true,
        visual: 'error',
        id: 'error-create-withdrawal-offer',
      });

      datadogErrorHelper({
        errorMessage:
          '[LBBW] Failed to create crypto transfer withdrawal offer.',
        errorSeverity: DatadogErrorLevels.CRITICAL,
      });
    }

    setIsLoading(false);
  };

  return {
    handlers: {
      changeAmountValueType,
      validateInputAndCheckForThresholdsErrors,
      onSubmit: onSubmitCallCreateOfferMutation,
    },
    formState: {
      isLoading,
      amountInputErrors,
      amountInputValue,
      amountInputValueWithMinFees,
      amountInputValueType,
    },
  };
};
